import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toNumberString } from '@client/toolkit';
import { ToggleButton, TopConsumptionItem } from '../../../../../components';
import { DashboardCardWrapper } from '../../../components/DashboardCardWrapper/dashboard-card-wrapper';
import { BuildingIcon } from '../../../../../components/icons/building-icon';
import { useGetEosConsumptionSpacesQuery } from '@client/api';
import { useDashboardFilters } from '../../../context/dashboard-filters.context';
import { NoDataWidgetContent } from '../../../dashboard-basic-view/components/NoDataWidget/no-data-widget';

export function TopConsumptionWidget() {
  const { t: tWidget } = useTranslation('widget');
  const { selectedSpaceIds, selectedDateFromIso, selectedDateToIso } = useDashboardFilters();
  const [dataType, setDataType] = useState<'kwh' | 'm2'>('kwh');

  const { data, isLoading, isError } = useGetEosConsumptionSpacesQuery(
    {
      type: dataType,
      from: selectedDateFromIso || '',
      to: selectedDateToIso || '',
      spaceIds: selectedSpaceIds,
    },
    {
      skip: !selectedDateFromIso || !selectedDateToIso,
    },
  );

  const spaces = data || [];
  const unit = dataType === 'kwh' ? 'kWh' : 'kWh/m²';
  const isMultipleSpaces = useMemo(() => {
    return !!(spaces.length >= 3);
  }, [spaces]);

  return (
    <DashboardCardWrapper
      to="/reporting/consumption-by-sensor"
      icon={<BuildingIcon />}
      title={isMultipleSpaces ? tWidget('topConsumption.titleMultiple') : tWidget('topConsumption.title')}
      titleRightContent={
        <ToggleButton
          condition1="kWh/m²"
          condition2="kWh"
          initialCondition={0}
          onChange={(value) => {
            setDataType(value === 0 ? 'm2' : 'kwh');
          }}
        />
      }
      loading={isLoading}
    >
      {(!isLoading || isError) && spaces.length === 0 ? (
        <NoDataWidgetContent noDataText={tWidget('energy.noData')} />
      ) : (
        <div className="flex flex-col gap-4">
          {spaces?.map((item) => (
            <TopConsumptionItem
              key={item.name}
              name={item.name ?? 'Unknown'}
              value={`${toNumberString(item.value ?? 0)} ${unit}`}
              percentage={`${item.percentage}`}
            />
          ))}
        </div>
      )}
    </DashboardCardWrapper>
  );
}
